import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import config from '../config/config.json';
import { toast } from 'react-hot-toast';

export interface Order {
  id: number;
  claimer_name: string;
  created_at: string;
  customer_email: string;
  customer_lastname: string;
  customer_name: string;
  d2r_shop_created_at: string;
  d2rs_order_id: string;
  d2rs_order_url: string;
  delivery_due_compensation: string;
  delivery_due_reward: string;
  sourcer_fg_due_reward: string;
  status: string;
  title: string;
  updated_at: string;
}

// TanStack Query hooks
export function useOrders() {
  const sessionToken = localStorage.getItem('session_token');
  return useQuery({
    queryKey: ['orders'],
    queryFn: async (): Promise<Order[]> => {
      const response = await axios.get<Order[]>(`${config.backend_base_url}d2rs/orders`, {
        headers: { 'session-token': sessionToken },
      });
      return response.data || [];
    },
    enabled: !!sessionToken,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 2,
  });
}

// Helper to filter orders by status
export function useFilteredOrders() {
  const { data: orders = [], isLoading, error } = useOrders();

  const filteredOrders = {
    unclaimed: orders.filter((order) => order.status === 'unclaimed'),
    inProgress: orders.filter((order) => order.status === 'in_progress'),
    waitingForDelivery: orders.filter((order) => order.status === 'waiting_for_delivery'),
    delivered: orders.filter((order) => order.status === 'delivered'),
    all: orders,
  };

  // Total ongoing orders (unclaimed + in_progress + waiting_for_delivery)
  const ongoingOrdersCount =
    filteredOrders.unclaimed.length +
    filteredOrders.inProgress.length +
    filteredOrders.waitingForDelivery.length;

  return {
    filteredOrders,
    ongoingOrdersCount,
    isLoading,
    error,
  };
}

// Function to notify about open orders
export function notifyAboutOpenOrders(count: number) {
  if (count > 0) {
    toast.success(`You have ${count} open orders to process`);
  }
}
